import { File } from "@shared-database/models/file";
import { User } from "@shared-database/models/user";
import { sharedSchema } from "@shared-database/pgSchema";
import {
  leoEnum,
  leoForeignID,
  leoID,
  timestampColumns,
} from "@shared-database/utils";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";
import { index, integer, text, timestamp } from "drizzle-orm/pg-core";

export const WorkerJobTypeEnumValues = [
  "PROCESS_FILE",
  "PROCESS_SHAREPOINT_FILE",
  "COMPUTE_HIGHLIGHTS",
  "EXTRACT_PROFILE_GRAPH",
  "DETECT_PROFILE_COMMUNITIES",
  "REINDEX_FILE",
] as const;
export type WorkerJobTypeEnumType = (typeof WorkerJobTypeEnumValues)[number];

export const WorkerJobStatusEnumValues = [
  "QUEUED",
  "STARTED",
  "SUCCEEDED",
  "FAILED",
] as const;
export type WorkerJobStatusEnumType =
  (typeof WorkerJobStatusEnumValues)[number];

/** Worker states that indicate a queued or currently running job. */
export const ActiveWorkerJobStatusEnumValues = [
  "QUEUED",
  "STARTED",
] as const satisfies readonly WorkerJobStatusEnumType[];
export type ActiveWorkerJobStatusEnumType =
  (typeof ActiveWorkerJobStatusEnumValues)[number];

export const workerJobTypeEnum = leoEnum(
  "WorkerJobType",
  WorkerJobTypeEnumValues,
);
export const workerJobStatusEnum = leoEnum(
  "WorkerJobStatus",
  WorkerJobStatusEnumValues,
);

/**
 * Durable audit row for asynchronous worker commands.
 *
 * Worker routes create/update these rows around queue dispatch and execution.
 * Optional context ids identify the resource being processed; profile graph
 * jobs use `aiProfileId`, file/document jobs use `fileId`, and SharePoint
 * sync work can additionally carry `sharepointPageId`.
 */
export const WorkerJob = sharedSchema.table(
  "WorkerJob",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),
    type: workerJobTypeEnum("type").notNull(),
    status: workerJobStatusEnum("status").notNull().default("QUEUED"),
    // References
    fileId: leoForeignID("fileId").references(() => File.id, {
      onDelete: "set null",
    }),
    userId: leoForeignID("userId").references(() => User.id, {
      onDelete: "set null",
    }),
    citationId: text("citationId"),
    /** Owning AI profile for profile graph extraction/community jobs. */
    aiProfileId: text("aiProfileId"),
    /** Profile SharePoint page associated with a sync/process job, if any. */
    sharepointPageId: text("sharepointPageId"),
    /** Workspace context retained for non-profile worker observability. */
    workspaceId: text("workspaceId"),
    correlationId: text("correlationId").notNull(),
    errorText: text("errorText"),
    attemptCount: integer("attemptCount").notNull().default(0),
    queuedAt: timestamp("queuedAt", { withTimezone: true }).notNull(),
    startedAt: timestamp("startedAt", { withTimezone: true }),
    completedAt: timestamp("completedAt", { withTimezone: true }),
  },
  (table) => [
    index("WorkerJob_createdAtIdx").on(table.createdAt),
    index("WorkerJob_statusIdx").on(table.status),
    index("WorkerJob_typeIdx").on(table.type),
    index("WorkerJob_fileIdIdx").on(table.fileId),
    index("WorkerJob_userIdIdx").on(table.userId),
    index("WorkerJob_correlationIdIdx").on(table.correlationId),
  ],
);

// Types
export type WorkerJobType = InferSelectModel<typeof WorkerJob>;
export type WorkerJobInsertType = InferInsertModel<typeof WorkerJob>;
